import type { Component } from "solid-js";
import { createMemo, For, getOwner, runWithOwner, Show, untrack } from "solid-js";
import { compact, flatten, isEmpty, isNil } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import EmptyState from "../../components/emptyState/EmptyState";
import type { Folder } from "../../types/Folder";
import type { ThemeName, CustomerInfo } from "../../types";
import { themeIconColour } from "../../types";
import { useCustomerState } from "../../state/customer";
import Handlebar from "../../components/field/Handlebar";
import type { IconName } from "../../components/icon/Icon";
import { Icon } from "../../components/icon/Icon";
import Text from "../../components/text/Text";
import type { Form } from "../../state/form";
import type { IconButtonMenuProps } from "../../components/listItemMenu/ListItemMenu";
import { IconButtonMenu } from "../../components/listItemMenu/ListItemMenu";
import ApiService from "../../services/ApiService";
import { A, useNavigate } from "@solidjs/router";
import Pill from "../../components/pill/Pill";
import DraggableList from "./dnd/DraggableList";
import Draggable from "./dnd/Draggable";
import DropIndicator from "./dnd/DropIndicator";
import FormId from "../../state/FormId";
import GenericFailureModal from "../GenericFailureModal";
import PermissionService from "../../services/PermissionService";
import FormatDate from "../../components/formatting/FormatDate";
import DeleteFormModal from "./folder/DeleteFormModal";
import CopyFormModal from "./folder/CopyFormModal";
import MoveFormModal from "./folder/MoveFormModal";
import { useModalManager } from "../../components/modal/ModalManager";

type FolderPageContentProps = {
  folder: Folder;
};

const FolderPageContentForms: Component<FolderPageContentProps> = (props) => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  let scrollContainerRef!: HTMLDivElement;

  const customersQuery = ApiService.getCustomers();
  const activeForms = createMemo(() => props.folder.forms.filter((form) => form.status === "ACTIVE"));

  return (
    <Show when={!isEmpty(activeForms())} fallback={<NoForms />}>
      <div class="flex h-full flex-col">
        <div class="mt-6 mb-0.5 flex h-8 gap-6 border-b border-subtle py-1.5 pr-4 pb-2 pl-5 text-base-semibold text-secondary">
          <div class="min-w-0 grow">{t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_TABLE_HEADER_FORM)}</div>
          <div class="w-46 flex-none">{t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_TABLE_HEADER_LAST_UPDATED)}</div>
          <div class="w-46 flex-none">{t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_TABLE_HEADER_STATUS)}</div>
          <div class="w-7.5" />
        </div>

        <div class="flex grow flex-col overflow-y-auto pb-6 focus-visible:outline-default" ref={scrollContainerRef}>
          <DraggableList
            scrollContainer={scrollContainerRef}
            monitorDraggableType="FORM"
            items={props.folder.forms.map((form) => ({ id: form.id.toString() }))}
            onDrop={(sourceData, index) => {
              runWithOwner(owner, () => {
                ApiService.reorderForm().mutate(
                  {
                    folderId: untrack(() => props.folder.id),
                    formId: FormId.from(sourceData.id),
                    toPosition: index,
                  },
                  {
                    onError: (err) => {
                      open({
                        content: () => (
                          <GenericFailureModal title={t(($) => $.REORDER_FORM_FAILURE_MODAL_TITLE)} error={err} />
                        ),
                      });
                    },
                  },
                );
              });
            }}
          >
            <ul>
              <For each={activeForms()}>
                {(form, index) => (
                  <li>
                    <Item
                      folder={props.folder}
                      form={form}
                      index={index()}
                      customers={customersQuery.data ?? []}
                      isLast={index() === activeForms().length - 1}
                    />
                  </li>
                )}
              </For>
            </ul>
          </DraggableList>
        </div>
      </div>
    </Show>
  );
};

const Item: Component<{ folder: Folder; form: Form; index: number; customers: CustomerInfo[]; isLast: boolean }> = (
  props,
) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const navigate = useNavigate();
  const publishedFormVersionId = createMemo(() => props.form.publishedVersion?.formVersionId);
  const moreThanOneFolderExists = createMemo(() => (ApiService.getFolders().data?.length ?? 0) > 1);

  let dragHandle!: HTMLDivElement;

  const actions = createMemo<IconButtonMenuProps<string>["items"]>(() => [
    compact([
      PermissionService.canEditForm(customer.customerId, props.form.id) && {
        value: "edit",
        type: "action",
        onClick: async () => {
          const formVersionId = await ApiService.resolveFormDraftId(props.form.id);
          navigate(`/customers/${customer.customerId}/forms/${props.form.id}/formVersions/${formVersionId}`);
        },
        variant: "icon",
        label: t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_ACTIONS_EDIT),
        icon: { name: "pencil", type: "outline" },
      },
      PermissionService.canCopyForm(customer.customerId) &&
        !isNil(publishedFormVersionId()) && {
          value: "copy",
          type: "action",
          onClick: () =>
            open({
              content: () => (
                <CopyFormModal
                  formId={props.form.id}
                  folderId={props.folder.id}
                  formName={props.form.meta.name}
                  customer={customer}
                  customers={props.customers}
                  formVersionId={publishedFormVersionId()!}
                />
              ),
            }),
          variant: "icon",
          label: t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_ACTIONS_COPY),
          icon: { name: "duplicate", type: "outline" },
        },
      PermissionService.canMoveForm(customer.customerId, props.folder.id, props.form.id) &&
        moreThanOneFolderExists() && {
          value: "move",
          type: "action",
          onClick: () =>
            open({
              content: () => <MoveFormModal folderId={props.folder.id} formId={props.form.id} />,
            }),
          variant: "icon",
          label: t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_ACTIONS_MOVE),
          icon: { name: "switchHorizontal", type: "outline" },
        },
      PermissionService.canDeleteForm(customer.customerId, props.form.id) && {
        value: "delete",
        type: "action",
        onClick: () =>
          open({
            content: () => (
              <DeleteFormModal
                customerId={customer.customerId}
                folderId={props.folder.id}
                formId={props.form.id}
                formName={props.form.meta.name}
              />
            ),
          }),
        variant: "icon",
        label: t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_ACTIONS_DELETE),
        icon: { name: "trash", type: "outline" },
      },
    ]),
  ]);

  return (
    <Draggable
      data={{
        type: "FORM",
        id: props.form.id.toString(),
        index: props.index,
      }}
      isLast={props.isLast}
      dragHandle={dragHandle}
      preview={() => (
        <DragPreview name={props.form.meta.name} icon={props.form.meta.icon} theme={props.form.meta.theme.name} />
      )}
    >
      {(state) => (
        <div
          class="rounded-sm bg-surface-default [&:not(:has([class*='bg-']:hover))]:hover:bg-surface-hover"
          classList={{ "opacity-50": state().type === "is-dragging" }}
        >
          <A
            class="absolute top-0 left-0 block size-full cursor-pointer -outline-offset-1 focus-visible:outline-default"
            href={`/customers/${customer.customerId}/folders/${props.folder.id}/forms/${props.form.id}`}
            aria-label={props.form.meta.name}
          />
          <div class="pointer-events-none flex items-center justify-between gap-6 p-4 pl-1.5">
            <div class="flex min-w-0 grow items-center gap-1.5">
              <div class="pointer-events-auto cursor-grab" ref={dragHandle}>
                <Handlebar
                  expandDragArea
                  classList={{
                    invisible: !PermissionService.canReorderForm(customer.customerId, props.folder.id, props.form.id),
                  }}
                />
              </div>

              <div class="flex size-10 flex-none items-center justify-center rounded-sm border border-subtle bg-surface-extra-subtle">
                <Icon
                  name={props.form.meta.icon}
                  type="solid"
                  size="lg"
                  class={themeIconColour[props.form.meta.theme.name]}
                />
              </div>
              <div class="flex max-w-full flex-col overflow-hidden">
                <Text class="text-base-semibold text-primary" text={props.form.meta.name} truncate />
                <Show when={props.form.meta.description}>
                  {(description) => <Text class="text-base-regular text-secondary" text={description()} truncate />}
                </Show>
              </div>
            </div>

            <div class="flex w-46 flex-none flex-col overflow-hidden text-base-regular text-secondary">
              <Show when={props.form.latestVersion}>
                {(latestVersion) => (
                  <>
                    <Show when={latestVersion().lastUpdated}>{(date) => <FormatDate value={date()} />}</Show>
                    <Text text={latestVersion().lastUpdatedBy} truncate />
                  </>
                )}
              </Show>
            </div>

            <div class="flex w-46 flex-none flex-col items-start gap-1">
              <Show
                when={props.form.publishedVersion}
                fallback={<Pill variant="neutral" label={t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_STATUS_UNPUBLISHED)} />}
              >
                <>
                  <Pill variant="success" label={t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_STATUS_PUBLISHED)} />
                  <Show when={props.form.latestVersion?.status === "DRAFT"}>
                    <Pill variant="neutral" label={t(($) => $.FOLDER_PAGE_FORMS_TAB_FORM_STATUS_UNPUBLISHED_CHANGES)} />
                  </Show>
                </>
              </Show>
            </div>

            <div
              class="pointer-events-auto relative flex items-center"
              classList={{
                invisible: isEmpty(flatten(actions())),
              }}
            >
              <IconButtonMenu aria-label={t(($) => $.ACTIONS_ARIA)} items={actions()} />
            </div>

            <Show when={state().type === "is-dragging-over" && !isNil(state().closestEdge)}>
              <DropIndicator edge={state().closestEdge!} gap={0} />
            </Show>
          </div>
        </div>
      )}
    </Draggable>
  );
};

type DragPreviewProps = { name: string; icon: IconName; theme: ThemeName };
const DragPreview = (props: DragPreviewProps) => (
  <div class="flex w-lg items-center gap-1.5 rounded-sm border border-default bg-surface-default py-2 pr-4 pl-1.5 shadow-elevation-light-3">
    <Handlebar />
    <div
      class={`flex size-8 flex-none items-center justify-center rounded-sm border border-subtle bg-surface-extra-subtle ${themeIconColour[props.theme]}`}
    >
      <Icon name={props.icon} type="solid" size="md" />
    </div>
    <Text class="text-base-semibold text-primary" text={props.name} truncate />
  </div>
);

const NoForms = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.FOLDER_PAGE_FORMS_TAB_EMPTY_STATE_TITLE)}
      description={t(($) => $.FOLDER_PAGE_FORMS_TAB_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "document", secondaryIcon: "search" }}
    />
  );
};

export default FolderPageContentForms;
